import type { DocumentNode } from "@apollo/client";
import type { PanelAction } from "../components/molecules/panel/panel";
import type { Services } from "../services/services";
import type { Controller } from "../util/controller";
import type { Disposable } from "../util/lifecycle";
import type { ValueOrI18nKeyProp } from "../util/type";
import type { JSX } from "react";
import { observable } from "ecce-react";


export type CrudOperationConfig = (
	& ValueOrI18nKeyProp<"title", string>
	& Partial<ValueOrI18nKeyProp<"submitLabel", string>>
	& Partial<ValueOrI18nKeyProp<"successMessage", string>>
	& {
		submitLabelColor?: PanelAction["color"];
		secondaryAction?: PanelAction | null | undefined;
		panelHeaderContent?: JSX.Element | null | undefined;
		fullHeightDialog?: boolean;
	}
);

export type CrudOperationOptions<EditResult> = Partial<{
	refetchQueries: DocumentNode[];
	onSubmitted: (result: EditResult) => void;
}>;

export type CrudSubmitContext = Readonly<{
	refetchQueries: DocumentNode[];
}>;

export namespace CrudOperationResult {
	/**
	 * Returned from {@link CrudOperation.submit()} when the operation succeeds.
	 *
	 * The operation is considered complete, and will be ended.
	 */
	export type Ok<T> = {
		status: "ok";

		/**
		 * The result of the operation.
		 */
		value: T;

		/**
		 * The i18n key for the success notification's title. If not provided, the
		 * default 'Saved' message will be used.
		 */
		notificationTitleKey?: string;
		/**
		 * The i18n values used to templating {@link CrudOperationResult.Ok.notificationTitleKey}.
		 */
		notificationTitleContext?: Record<string, unknown>;
	};


	/**
	 * Returned from {@link CrudOperation.submit()} when the operation was unable
	 * to be completed due to invalid user input.
	 *
	 * The operation will not be ended, and the operation is responsible for
	 * displaying the validation errors to the user.
	 */
	export type Invalid = {
		status: "invalid";
	};

	/**
	 * Returned from {@link CrudOperation.submit()} when the operation would result
	 * in no changes being made.
	 *
	 * The operation is considered complete, and will be ended.
	 */
	export type Unchanged = {
		status: "unchanged";

		/**
		 * The i18n key for the info notification's title. If not provided, the
		 * default 'No changes to save' message will be used.
		 */
		notificationTitleKey?: string;

		/**
		 * The i18n values used to templating {@link CrudOperationResult.Unchanged.notificationTitleKey}.
		 */
		notificationTitleContext?: Record<string, unknown>;
	};

	export const ok = <T>(result: T, options?: Omit<CrudOperationResult.Ok<T>, "status" | "result">): CrudOperationResult.Ok<T> => {
		return ({
			status: "ok",
			value: result,
			...options,
		});
	};

	export const invalid = (): CrudOperationResult.Invalid => ({
		status: "invalid",
	});

	export const unchanged = (options?: Omit<CrudOperationResult.Unchanged, "status">): CrudOperationResult.Unchanged => ({
		status: "unchanged",
		...options,
	});
}

export type CrudOperationResult<T> = (
	| CrudOperationResult.Ok<T>
	| CrudOperationResult.Invalid
	| CrudOperationResult.Unchanged
);

/**
 * Base class for a CRUD operation which can be managed by the CrudService.
 */
export abstract class CrudOperation<T> implements Disposable {
	readonly #config: Readonly<CrudOperationConfig>;

	#services: Services | null = null;
	#options: CrudOperationOptions<T> | null = null;

	abstract readonly view: () => JSX.Element;

	#showActions: boolean = true;
	get showActions(): boolean { return this.#showActions; }
	@observable() private set showActions(value: boolean) { this.#showActions = value; }

	constructor(config: CrudOperationConfig) {
		this.#config = config;
	}

	/**
	 * Called by CrudService.
	 */
	private initialiseOperation(services: Services, options: CrudOperationOptions<T>) {
		this.#services = services;
		this.#options = options;
	}

	/**
	 * Setup the initial state of the operation.
	 *
	 * The dialog will be showing a loading spinner until this method resolves.
	 */
	abstract initialise(): Promise<void>;

	/**
	 * Called when the user submits the form.
	 *
	 * Return the value representing the result of the operation, or null if no
	 * changes were submitted.
	 */
	abstract submit(options: CrudSubmitContext): Promise<CrudOperationResult<T>>;

	/**
	 * Called when the operation has finished, either from submission or
	 * cancellation. Clean up anything you need to here.
	 */
	abstract dispose(): void;

	get title(): string {
		return this.services.translation.resolveValueOrI18nKey(
			this.#config.title, this.#config.titleKey ?? "common:edit", this.#config.titleValues
		);
	}

	get submitLabel(): string {
		return this.services.translation.resolveValueOrI18nKey(
			this.#config.submitLabel, this.#config.submitLabelKey ?? "common:save", this.#config.submitLabelValues
		);
	}

	get successMessage(): string | JSX.Element {
		return this.services.translation.resolveValueOrI18nKey(
			this.#config.successMessage, this.#config.successMessageKey ?? "common:saved", this.#config.successMessageValues
		);
	}

	get submitLabelColor(): PanelAction["color"] {
		return this.#config.submitLabelColor ?? "primary";
	}

	get secondaryAction(): PanelAction | null {
		return this.#config.secondaryAction ?? null;
	}

	get panelHeaderContent(): JSX.Element | null {
		return this.#config.panelHeaderContent ?? null;
	}

	/**
	 * Access to the application's services.
	 */
	protected get services(): Services {
		if(!this.#services) {
			throw new Error("Operation was not initialised.");
		}

		return this.#services;
	}

	/**
	 * The `refetchQueries` option passed when beginning this operation.
	 */
	protected get refetchQueries(): DocumentNode[] | undefined {
		if(!this.#options)	 {
			throw new Error("Operation was not initialised.");
		}

		return this.#options.refetchQueries;
	}

	protected initialiseController(...controllers: Controller[]): void {
		for(const controller of controllers) {
			controller["injectServices"](this.services);
			controller["runInit"]();
		}
	}

	/**
	 * Enables the CrudService's `loading` state.
	 * @returns a callback which ends the loading state
	 *
	 * @example
	 * const endLoading = this.beginLoading();
	 * try {
	 *   await doSomeInteractionBlockingWork();
	 * } finally {
	 *   endLoading()
	 * }
	 */
	protected beginLoading(): VoidFunction {
		this.services?.crud["setLoading"](true);
		return () => this.services.crud["setLoading"](false);
	}

	protected setShowActions(show: boolean): void {
		this.showActions = show;
	}

	protected get fullHightDialog(): boolean {
		return this.#config.fullHeightDialog ?? false;
	}

	hasChanges(): boolean {
		return true;
	}
}
